import { useState, useEffect } from 'react';
import { obtenerActividadesProfesor, actualizarActividad, eliminarActividad } from '../../../services/ActividadesService';
import moment from 'moment';
import { toast } from 'react-toastify';

export const useActividades = () => {
    const [actividades, setActividades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtroActual, setFiltroActual] = useState('niño');
    const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
    const [dialogoAbierto, setDialogoAbierto] = useState(false);
    const [modalCrearVisible, setModalCrearVisible] = useState(false);
    const [menuAbierto, setMenuAbierto] = useState(false);
    const [modalEditarVisible, setModalEditarVisible] = useState(false);
    const [confirmacionEliminarVisible, setConfirmacionEliminarVisible] = useState(false);
    const [observacionesDialogVisible, setObservacionesDialogVisible] = useState(false);
    const [observaciones, setObservaciones] = useState('');
    const [actividadParaCompletar, setActividadParaCompletar] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});

    const cargarActividades = async () => {
        try {
            const userId = JSON.parse(localStorage.getItem('user')).id;
            const response = await obtenerActividadesProfesor(userId);
            
            if (!response.actividades || response.actividades.length === 0) {
                setActividades({});
                setLoading(false);
                return;
            }

            const actividadesAgrupadas = response.actividades.reduce((acc, actividad) => {
                const childId = actividad.id_child;
                if (!acc[childId]) {
                    acc[childId] = {
                        detallesNino: actividad.detallesNino,
                        actividades: []
                    };
                }
                acc[childId].actividades.push(actividad);
                return acc;
            }, {});

            setActividades(actividadesAgrupadas);
        } catch (err) {
            console.error('Error al cargar actividades:', err);
            setError('No hay actividades registradas');
        } finally {
            setLoading(false);
        }
    };

    // Nueva función para agrupar por actividades
    const agruparPorActividades = (actividadesOriginales) => {
        const actividadesPorNombre = {};
        
        Object.values(actividadesOriginales).forEach(({actividades: actividadesNino}) => {
            actividadesNino.forEach(actividad => {
                const key = `${actividad.nombre}_${actividad.fecha_programada}`;
                if (!actividadesPorNombre[key]) {
                    actividadesPorNombre[key] = {
                        nombre: actividad.nombre,
                        descripcion: actividad.descripcion,
                        fecha_programada: actividad.fecha_programada,
                        participantes: [],
                        estado_general: 'pendiente'
                    };
                }
                
                actividadesPorNombre[key].participantes.push({
                    ...actividad,
                    detallesNino: actividad.detallesNino
                });
                
                // Calcular estado general
                const completadas = actividadesPorNombre[key].participantes.filter(p => p.estado === 'completada').length;
                const total = actividadesPorNombre[key].participantes.length;
                
                if (completadas === total) {
                    actividadesPorNombre[key].estado_general = 'completada';
                } else if (completadas > 0) {
                    actividadesPorNombre[key].estado_general = 'parcial';
                } else {
                    actividadesPorNombre[key].estado_general = 'pendiente';
                }
            });
        });
        
        return actividadesPorNombre;
    };

    // Función para completar actividad de un participante específico
    const completarActividadParticipante = async (actividad, participante) => {
        setActividadParaCompletar(participante);
        setObservacionesDialogVisible(true);
    };

    // Función para completar actividad para todos los participantes
    const completarActividadCompleta = async (actividadGrupo) => {
        try {
            const observacionesGlobales = prompt('Observaciones para todos los participantes (opcional):') || '';
            
            for (const participante of actividadGrupo.participantes) {
                if (participante.estado !== 'completada') {
                    const datosActualizados = {
                        ...participante,
                        estado: 'completada',
                        observaciones: observacionesGlobales,
                        fecha_completado: moment().format('YYYY-MM-DD')
                    };
                    
                    await actualizarActividad(participante.id_actividad, datosActualizados);
                }
            }
            
            await cargarActividades();
            toast.success('Actividad completada para todos los participantes');
        } catch (error) {
            console.error('Error al completar la actividad:', error);
            toast.error('Error al completar la actividad');
        }
    };

    useEffect(() => {
        cargarActividades();
    }, []);

    const toggleExpand = (groupId) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const abrirDialogo = (actividad) => {
        setActividadSeleccionada(actividad);
        setDialogoAbierto(true);
        setMenuAbierto(false);
    };

    const cerrarDialogo = () => {
        setDialogoAbierto(false);
        setActividadSeleccionada(null);
        setMenuAbierto(false);
    };

    const handleEliminar = async () => {
        setConfirmacionEliminarVisible(true);
    };

    const confirmarEliminar = async () => {
        try {
            await eliminarActividad(actividadSeleccionada.id_actividad);
            toast.success('Actividad eliminada correctamente');
            await cargarActividades();
            setConfirmacionEliminarVisible(false);
            cerrarDialogo();
        } catch (error) {
            console.error('Error al eliminar la actividad:', error);
            toast.error('Error al eliminar la actividad');
        }
    };

    const handleEditar = () => {
        setModalEditarVisible(true);
        setDialogoAbierto(false);
    };

    const toggleMenu = () => {
        setMenuAbierto(prev => !prev);
    };

    const handleCompletarActividad = async (actividad, e) => {
        e.stopPropagation();
        setActividadParaCompletar(actividad);
        setObservacionesDialogVisible(true);
    };

    const confirmarCompletarActividad = async () => {
        try {
            const datosActualizados = {
                ...actividadParaCompletar,
                estado: 'completada',
                observaciones: observaciones,
                fecha_completado: moment().format('YYYY-MM-DD')
            };

            await actualizarActividad(actividadParaCompletar.id_actividad, datosActualizados);
            await cargarActividades();
            toast.success('Actividad completada correctamente');
            setObservacionesDialogVisible(false);
            setActividadParaCompletar(null);
            setObservaciones('');
        } catch (error) {
            console.error('Error al completar la actividad:', error);
            toast.error('Error al completar la actividad');
        }
    };

    const esFechaVencida = (fechaProgramada) => {
        const fechaActual = moment().startOf('day');
        const fechaProgramadaDate = moment(fechaProgramada).startOf('day');
        return fechaProgramadaDate.isBefore(fechaActual);
    };

    const truncarDescripcion = (descripcion, limite = 50) => {
        if (!descripcion) return '';
        if (descripcion.length <= limite) return descripcion;
        return descripcion.substring(0, limite) + '...';
    };

    return {
        actividades,
        loading,
        error,
        filtroActual,
        actividadSeleccionada,
        dialogoAbierto,
        modalCrearVisible,
        menuAbierto,
        modalEditarVisible,
        confirmacionEliminarVisible,
        observacionesDialogVisible,
        observaciones,
        actividadParaCompletar,
        expandedGroups,
        setFiltroActual,
        setModalCrearVisible,
        setModalEditarVisible,
        setObservaciones,
        toggleExpand,
        abrirDialogo,
        cerrarDialogo,
        handleEliminar,
        confirmarEliminar,
        handleEditar,
        toggleMenu,
        handleCompletarActividad,
        confirmarCompletarActividad,
        esFechaVencida,
        truncarDescripcion,
        cargarActividades,
        setConfirmacionEliminarVisible,
        setObservacionesDialogVisible,
        setActividadParaCompletar,
        agruparPorActividades,
        completarActividadParticipante,
        completarActividadCompleta
    };
};